// What saving the drawer's edit will do, decided once, typed, and rendered by
// the drawer.
//
// Content (copy, destination, assets) is immutable per revision on the
// server: an edit of an approved revision, or of one a published campaign
// pins, mints the next revision rather than being refused (0023). Name and
// agency are header metadata and always land in place. The button and the
// notice above it say which of those the click is about to be, and who keeps
// what — before the click, in words a phone can show.

import type { Creative, CreativePin } from '@/api/types';
import { campaignStatusLabel, campaignPinKind } from '@/lib/campaign-status';

export type SaveKind = 'metadata_only' | 'in_place' | 'new_revision' | 'nothing';

export interface RevisionSavePlan {
  kind: SaveKind;
  /** The revision a `new_revision` save will mint. */
  nextRevision: number;
  /** The sentence for the strip above the footer, or null for a quiet save. */
  notice: string | null;
  buttonLabel: string;
}

export interface EditBody {
  spec?: string;
  destination_url?: string;
  name?: string;
  agency?: string | null;
}

type Editable = Pick<
  Creative,
  'status' | 'revision' | 'spec' | 'destination_url' | 'name' | 'agency' | 'channel'
> & { pinned_by?: CreativePin[] | null };

function contentChanged(creative: Editable, body: EditBody): boolean {
  return (
    (body.spec !== undefined && body.spec !== creative.spec) ||
    (body.destination_url !== undefined && body.destination_url !== creative.destination_url)
  );
}

function metadataChanged(creative: Editable, body: EditBody): boolean {
  return (
    (body.name !== undefined && body.name !== creative.name) ||
    (body.agency !== undefined && (body.agency ?? '') !== (creative.agency ?? ''))
  );
}

/** The campaigns that pin the creative's current revision, live ones first. */
function currentPins(creative: Editable): CreativePin[] {
  const pins = (creative.pinned_by ?? []).filter(
    (pin) => pin.creative_revision === creative.revision,
  );
  const rank = (pin: CreativePin) =>
    campaignPinKind(pin) === 'live' ? 0 : ['paused', 'paused_by_system'].includes(campaignPinKind(pin)) ? 1 : 2;
  return [...pins].sort((a, b) => rank(a) - rank(b));
}

function others(n: number): string {
  return n === 1 ? '1 more' : `${n} more`;
}

export function revisionSavePlan(creative: Editable, body: EditBody): RevisionSavePlan {
  const next = creative.revision + 1;
  const content = contentChanged(creative, body);
  const metadata = metadataChanged(creative, body);
  if (!content) {
    return {
      kind: metadata ? 'metadata_only' : 'nothing',
      nextRevision: next,
      notice: null,
      buttonLabel: 'Save changes',
    };
  }
  const pins = currentPins(creative);
  const [first, ...rest] = pins;
  if (first) {
    const lead = `${first.campaign_name}`;
    const status = campaignStatusLabel(campaignPinKind(first));
    const tail = rest.length ? ` and ${others(rest.length)}` : '';
    // A roll-out moves a *published* campaign's pin. A campaign that is still
    // ready to publish has nothing to roll out to, so naming that verb sent
    // operators to a route that refuses it. A failed Meta publish is re-pinned
    // by an admin instead (2026-10-02: three campaigns pinned a "Book Now"
    // revision Graph refuses at create_ad).
    const keeps =
      first.campaign_status !== 'ready_to_publish'
        ? `${lead} keeps version ${creative.revision} until you roll it out.`
        : creative.channel === 'meta'
          ? `${lead} keeps version ${creative.revision}. If its Meta publish failed, approve version ${next} and ask an admin to re-pin it from Admin → Remediations (Re-pin a failed Meta publish).`
          : `${lead} keeps version ${creative.revision}.`;
    const notice =
      first.campaign_status === 'draft'
        ? `Draft campaign ${lead}${tail} uses version ${creative.revision}. Saving creates version ${next}; ${lead} keeps version ${creative.revision}.`
        : `This creative is ${status} in ${lead}${tail}. Saving creates version ${next} as a draft; ${keeps}`;
    return {
      kind: 'new_revision',
      nextRevision: next,
      notice,
      buttonLabel: `Save as version ${next}`,
    };
  }
  if (creative.status === 'approved') {
    return {
      kind: 'new_revision',
      nextRevision: next,
      notice: `Version ${creative.revision} is approved. Saving creates version ${next} as a draft; version ${creative.revision} stays available to campaigns.`,
      buttonLabel: `Save as version ${next}`,
    };
  }
  return {
    kind: 'in_place',
    nextRevision: next,
    notice:
      creative.channel === 'meta' && creative.status === 'review'
        ? 'Saving regenerates the Meta previews for this version.'
        : null,
    buttonLabel: 'Save changes',
  };
}
